import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

import { COPY } from "./copy";
import { buildGoogleConsentUrl, hasGoogleClientId } from "./googleOAuthUrl";
import { ProviderMark } from "./ProviderMark";

/**
 * The live "Connect Google Analytics" step. Builds the consent URL in the browser
 * and hands it to Google — no backend round trip, since nothing in the
 * authorization request is secret.
 *
 * A full-page redirect rather than a popup: popups get blocked, and the wizard
 * already persists its state, so the round trip costs nothing.
 */
export function Ga4Connect({
    connected = false,
    onDisconnect,
    onBeforeRedirect
}: {
    connected?: boolean;
    onDisconnect?: () => void;
    onBeforeRedirect?: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const connect = () => {
        if (busy) return;
        if (!hasGoogleClientId()) {
            // Misconfigured build rather than a user error — say so plainly.
            setError("Google sign-in isn't configured for this environment (missing client ID).");
            return;
        }
        setBusy(true);
        setError(null);

        // Let the caller persist wizard state before we leave the page.
        onBeforeRedirect?.();
        window.location.href = buildGoogleConsentUrl();
    };

    if (connected) {
        return (
            <div>
                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <CheckCircle2 className="h-4 w-4 flex-none text-emerald-600" strokeWidth={2} />
                    <div className="min-w-0 flex-1">
                        {/* The exchange returns tokens, not an account email — so we
                            can't name the account here yet. */}
                        <p className="text-[13px] font-semibold text-emerald-900">Connected to Google Analytics</p>
                        <p className="text-[12px] text-emerald-800">{COPY.wizard.step1.connectedScope}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => onDisconnect?.()}
                        className="rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-[12px] font-medium text-emerald-800 transition-colors hover:bg-emerald-50">
                        {COPY.wizard.step1.disconnect}
                    </button>
                </div>

                {/* TODO: replace with the real picker once googleAnalyticsSetupDetails
                    is wired — it needs the nested probe types before it can be queried. */}
                <p className="mt-3 text-[12.5px] text-gray-500">
                    Choosing which properties to read from is not wired up yet.
                </p>
            </div>
        );
    }

    return (
        <div>
            <button
                type="button"
                onClick={connect}
                disabled={busy}
                className="inline-flex items-center gap-2.5 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-[13px] font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : <ProviderMark />}
                {busy ? COPY.wizard.step1.connecting : COPY.wizard.step1.connectCta}
            </button>
            <p className="mt-3 text-[12.5px] text-gray-500">{COPY.wizard.step1.reassurance}</p>

            {error && (
                <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3.5">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600" strokeWidth={2} />
                    <div>
                        <p className="text-[12.5px] leading-relaxed text-amber-900">{error}</p>
                        <button
                            type="button"
                            onClick={connect}
                            className="mt-2.5 rounded-md bg-amber-600 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-amber-700">
                            {COPY.wizard.oauth.errors.DENIED.action}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Ga4Connect;
